/**
 * useCanMessageHost — ¿se le puede escribir a este anfitrión, y si no, por qué?
 *
 * POR QUÉ EXISTE. La ficha del carro ofrecía "escribirle al anfitrión", la base
 * rechazaba la conversación con 42501 y la pantalla mostraba "No tenés permisos
 * para esta acción". Dos veces, porque nada desactivaba el botón. Ese mensaje
 * miente sobre la causa —no es un permiso, es que todavía no hay reserva
 * pagada—, no dice qué hacer después, y suena a que la persona hizo algo
 * indebido.
 *
 * Y no se arregla mejorando el texto del servidor: `errorMessage()` solo muestra
 * `err.message` bajo `__DEV__`, así que en producción gana la clave i18n
 * empacada en el binario. La única salida es preguntar ANTES.
 *
 * LA REGLA NO SE ABLANDA. Sigue sin poderse hablar con nadie hasta que la
 * reserva está pagada — es la defensa contra la desintermediación y es
 * innegociable. Lo que cambia es que la app deje de invitar a una acción que va
 * a negar: en vez de un callejón sin salida, dice qué falta y ofrece el paso que
 * lo desbloquea.
 *
 * FALLA ABIERTA. Ante un error de red o caché frío, `resolved` se queda en false
 * y quien llama deja la pantalla como estaba. Bloquear a alguien que SÍ puede
 * escribir —porque se cayó una consulta— es peor que dejarlo tocar un botón que
 * el servidor va a responder de todos modos. El candado de verdad vive en el
 * trigger de Postgres, no acá; esto es cortesía, no seguridad.
 */
import { useQuery } from '@tanstack/react-query'
import { supabase } from '../supabase'
import { useAuth } from './use-auth'

export interface UseCanMessageHostResult {
  /** True una vez que hay respuesta definitiva. Nunca decidas nada mientras sea false. */
  resolved: boolean
  /** Solo significa algo cuando `resolved`. */
  allowed: boolean
  isLoading: boolean
}

export const canMessageHostKey = (viewerId: string, hostId: string) =>
  ['can-message-host', viewerId, hostId] as const

export function useCanMessageHost(hostId: string | null | undefined): UseCanMessageHostResult {
  const { user, isLoading: authLoading } = useAuth()
  const viewerId = user?.id ?? ''
  const host = hostId ?? ''

  const query = useQuery({
    queryKey: canMessageHostKey(viewerId, host),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('can_message_host', { p_host_id: host })
      if (error) throw error
      return data === true
    },
    enabled: !authLoading && !!viewerId && !!host,
    // Corto a propósito: la pantalla a la que se vuelve justo después de pagar
    // es esta. Un "todavía no podés escribirle" contra alguien que acaba de
    // pagar es la misma mentira en el otro sentido.
    staleTime: 15_000,
  })

  return {
    resolved: !authLoading && !!viewerId && !!host && query.isSuccess,
    allowed: query.data === true,
    isLoading: authLoading || query.isLoading,
  }
}
